import React from 'react';
import { motion } from 'framer-motion';
import { 
  Globe, 
  Layers, 
  Cpu, 
  Scissors, 
  LayoutDashboard, 
  Calendar, 
  Cloud, 
  BrainCircuit 
} from 'lucide-react';

export default function Services({ onSelectService }) {
  const services = [
    {
      icon: Globe,
      title: 'Website Development',
      description: 'Ultra-fast, responsive web interfaces powered by React & Next.js. Handcrafted with SEO architecture, modern micro-interactions, and premium UI designs that captivate.',
      color: 'from-[#8B6B4A]/10 to-[#C8A97E]/5',
      glowColor: 'group-hover:shadow-[#8B6B4A]/5',
      iconColor: 'text-[#8B6B4A]',
      borderHover: 'group-hover:border-[#8B6B4A]/30'
    },
    {
      icon: Layers,
      title: 'Full Stack Applications',
      description: 'End-to-end engineered software utilizing robust backends and lightning-fast frontends. Designed for zero-downtime, perfect synchronization, and optimal performance.',
      color: 'from-[#C8A97E]/10 to-[#D6C2A8]/5',
      glowColor: 'group-hover:shadow-[#C8A97E]/5',
      iconColor: 'text-[#C8A97E]',
      borderHover: 'group-hover:border-[#C8A97E]/30'
    },
    {
      icon: Cpu,
      title: 'SaaS Platforms',
      description: 'Scalable subscription platforms designed to automate operations, handle high concurrent loads, manage secure user workspaces, and monetize products efficiently.',
      color: 'from-[#8B6B4A]/10 to-[#D6C2A8]/5',
      glowColor: 'group-hover:shadow-[#8B6B4A]/5',
      iconColor: 'text-[#8B6B4A]',
      borderHover: 'group-hover:border-[#8B6B4A]/30'
    },
    {
      icon: Scissors,
      title: 'Salon Management Systems',
      description: 'Enterprise-grade booking, client records, and service tracking modules designed for hair studios and wellness centers. Complete checkout integration built-in.',
      color: 'from-[#C8A97E]/10 to-[#8B6B4A]/5',
      glowColor: 'group-hover:shadow-[#C8A97E]/5',
      iconColor: 'text-[#C8A97E]',
      borderHover: 'group-hover:border-[#C8A97E]/30'
    },
    {
      icon: LayoutDashboard,
      title: 'Admin Dashboards',
      description: 'Clean data visualizations, real-time KPI metrics, role-based user management, and seamless control panels that put full company coordination in your hands.',
      color: 'from-[#8B6B4A]/10 to-[#C8A97E]/5',
      glowColor: 'group-hover:shadow-[#8B6B4A]/5',
      iconColor: 'text-[#8B6B4A]',
      borderHover: 'group-hover:border-[#8B6B4A]/30'
    },
    {
      icon: Calendar,
      title: 'Appointment Systems',
      description: 'Frictionless, live scheduling slots with instant email/SMS reminders, employee availability roster sync, and calendar block management algorithms.',
      color: 'from-[#C8A97E]/10 to-[#D6C2A8]/5',
      glowColor: 'group-hover:shadow-[#C8A97E]/5',
      iconColor: 'text-[#C8A97E]',
      borderHover: 'group-hover:border-[#C8A97E]/30'
    },
    {
      icon: Cloud,
      title: 'Cloud Deployment',
      description: 'Auto-scalable DevOps setups on AWS and GCP. Configured with Docker containers, SSL integration, CI/CD automated pipelines, and 24/7 security auditing.',
      color: 'from-[#8B6B4A]/10 to-[#D6C2A8]/5',
      glowColor: 'group-hover:shadow-[#8B6B4A]/5',
      iconColor: 'text-[#8B6B4A]',
      borderHover: 'group-hover:border-[#8B6B4A]/30'
    },
    {
      icon: BrainCircuit,
      title: 'AI Integration',
      description: 'Empower your software systems with natural language agents, smart predictive recommendations, custom workflow automation engines, and machine learning power.',
      color: 'from-[#C8A97E]/10 to-[#8B6B4A]/5',
      glowColor: 'group-hover:shadow-[#C8A97E]/5',
      iconColor: 'text-[#C8A97E]',
      borderHover: 'group-hover:border-[#C8A97E]/30'
    }
  ];

  const handleCardClick = (title) => {
    if (onSelectService) {
      onSelectService(title);
    }
    const element = document.querySelector('#contact');
    if (element) {
      const offset = 80;
      const bodyRect = document.body.getBoundingClientRect().top;
      const elementRect = element.getBoundingClientRect().top;
      const elementPosition = elementRect - bodyRect;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' }
    }
  };

  return (
    <section id="services" className="py-24 md:py-32 relative bg-[#F8F5F0]" data-testid="services-section">
      {/* Background blobs */}
      <div className="absolute top-1/3 right-0 glowing-blob blob-beige opacity-[0.06]" />
      <div className="absolute bottom-1/3 left-0 glowing-blob blob-gold opacity-[0.05]" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5 }}
            className="inline-block text-xs font-semibold tracking-widest uppercase text-transparent bg-clip-text bg-gradient-to-r from-[#8B6B4A] to-[#C8A97E] font-poppins mb-3"
            data-testid="services-label"
          >
            Digital Architecture
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold font-space text-[#3E2F24] tracking-tight"
            data-testid="services-heading"
          >
            Bespoke Technical Offerings
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-[#6B625B] text-base sm:text-lg font-poppins max-w-2xl mx-auto mt-4"
            data-testid="services-description"
          >
            We build and code premium software applications tailored with precise layouts, clean database logic, and fluid frontends.
          </motion.p>
        </div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              variants={cardVariants}
              onClick={() => handleCardClick(service.title)}
              className={`group relative rounded-3xl p-6 transition-all duration-300 bg-white/40 border border-[#8B6B4A]/10 hover:bg-white/90 hover:scale-[1.01] ${service.borderHover} hover:shadow-lg ${service.glowColor} overflow-hidden cursor-pointer`}
              data-testid={`service-card-${index}`}
            >
              {/* Inner Gradient Accent */}
              <div className={`absolute -right-16 -top-16 w-36 h-36 bg-gradient-to-br ${service.color} rounded-full blur-2xl group-hover:scale-150 transition-all duration-500 opacity-60`} />

              <div className="relative z-10 flex flex-col h-full justify-between">
                <div>
                  {/* Icon Box */}
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-white border border-[#8B6B4A]/10 ${service.iconColor} mb-6 group-hover:scale-105 group-hover:border-[#8B6B4A]/30 transition-all duration-300`}>
                    <service.icon className="w-5 h-5" />
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold font-space text-[#3E2F24] mb-3" data-testid={`service-title-${index}`}>
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-[#6B625B] font-poppins text-xs sm:text-sm leading-relaxed" data-testid={`service-desc-${index}`}>
                    {service.description}
                  </p>
                </div>

                {/* Corner accent line */}
                <div className="w-0 h-0.5 bg-gradient-to-r from-transparent via-[#C8A97E] to-[#8B6B4A] group-hover:w-full transition-all duration-500 mt-6" />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}